/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Report-reason picker. Opened from Chat Info → Report. Lets the reporter pick a
// categorical reason and optionally consent to including the cited message
// content, then submits — replacing the old hardcoded `reason:'other',
// consent:false` report (C5). The host owns the actual `POST /v1/reports`.

import React, { useState } from "react";
import { Modal, Pressable, StyleSheet, Text, View } from "react-native";
import { Check, X } from "lucide-react-native";
import { useColorScheme } from "../foundations/colorScheme";
import { tokens } from "../design-system/tokens";
import { WebPortal } from "../chat/composer/WebPortal";

const IS_WEB = typeof document !== "undefined" && typeof window !== "undefined";

// Mirrors the backend's categorical reason set (abuse-and-trust.md §5.1 /
// services/api/internal/abuse `validReasons`).
export type ReportReason =
  | "spam"
  | "harassment"
  | "scam"
  | "csam"
  | "self_harm"
  | "violence"
  | "other";

const REASONS: ReadonlyArray<{ readonly value: ReportReason; readonly label: string }> = [
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "scam", label: "Scam or fraud" },
  { value: "csam", label: "Child sexual abuse material" },
  { value: "self_harm", label: "Self-harm or suicide" },
  { value: "violence", label: "Violence or threats" },
  { value: "other", label: "Something else" },
];

export interface ReportReasonSheetProps {
  readonly open: boolean;
  /** Display name of the reported user (for the prompt). */
  readonly reportedName?: string | undefined;
  /** Whether to offer the "include the message text" consent toggle. */
  readonly canIncludeContent?: boolean | undefined;
  readonly onSubmit: (reason: ReportReason, consent: boolean) => void;
  readonly onClose: () => void;
}

export const ReportReasonSheet = React.memo<ReportReasonSheetProps>(
  ({ open, reportedName, canIncludeContent = false, onSubmit, onClose }) => {
    const scheme = useColorScheme();
    const c = tokens.color.semantic[scheme === "dark" ? "dark" : "light"];
    const [reason, setReason] = useState<ReportReason | null>(null);
    const [consent, setConsent] = useState(false);

    if (!open) return null;

    const card = (
      <Pressable
        accessibilityRole="none"
        onPress={onClose}
        className="flex-1 items-center justify-center bg-black/70 p-6"
      >
        <Pressable
          accessibilityRole="none"
          onPress={(e) => e.stopPropagation?.()}
          className="w-full max-w-[420px] rounded-[16px] overflow-hidden bg-light-surface-primary dark:bg-[#15171C] border border-light-border-secondary dark:border-white/[0.06]"
        >
          <View className="flex-row items-center justify-between px-lg pt-lg pb-md">
            <Text className="font-sans font-bold text-headline text-light-text-primary dark:text-white">
              {reportedName !== undefined && reportedName.length > 0
                ? `Report ${reportedName}`
                : "Report"}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close"
              onPress={onClose}
              hitSlop={8}
              className="w-8 h-8 rounded-lg items-center justify-center hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]"
            >
              <X size={16} strokeWidth={2} color={c.icon.muted} />
            </Pressable>
          </View>

          <Text className="px-lg pb-sm font-sans text-[13px] text-light-text-tertiary dark:text-[#B5BAC3]">
            Why are you reporting this? Reports are confidential.
          </Text>

          <View className="px-sm pb-sm">
            {REASONS.map((r) => {
              const selected = reason === r.value;
              return (
                <Pressable
                  key={r.value}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  accessibilityLabel={r.label}
                  onPress={() => setReason(r.value)}
                  className="flex-row items-center justify-between px-md py-3 rounded-[10px] hover:bg-light-surface-tertiary dark:hover:bg-white/[0.05]"
                >
                  <Text className="flex-1 font-sans text-[14px] text-light-text-primary dark:text-white">
                    {r.label}
                  </Text>
                  {selected ? <Check size={16} strokeWidth={2.5} color={c.icon.default} /> : null}
                </Pressable>
              );
            })}
          </View>

          {canIncludeContent ? (
            <Pressable
              accessibilityRole="checkbox"
              accessibilityState={{ checked: consent }}
              accessibilityLabel="Include the message text in this report"
              onPress={() => setConsent((v) => !v)}
              className="flex-row items-center gap-sm px-lg py-3"
            >
              <View
                className={
                  "w-[18px] h-[18px] rounded-[5px] items-center justify-center border " +
                  (consent
                    ? "bg-light-accent-primary border-light-accent-primary dark:bg-white dark:border-white"
                    : "border-light-border-secondary dark:border-white/30")
                }
              >
                {consent ? <Check size={12} strokeWidth={3} color={c.icon.onAccent} /> : null}
              </View>
              <Text className="flex-1 font-sans text-[13px] text-light-text-secondary dark:text-[#B5BAC3]">
                Include the message text so our team can review it
              </Text>
            </Pressable>
          ) : null}

          <View className="flex-row justify-end gap-sm px-lg py-md">
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              onPress={onClose}
              className="px-lg py-2 rounded-[8px] hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]"
            >
              <Text className="font-sans text-[14px] text-light-text-secondary dark:text-[#B5BAC3]">
                Cancel
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Submit report"
              disabled={reason === null}
              onPress={() => {
                if (reason !== null) onSubmit(reason, consent);
              }}
              className={
                "px-lg py-2 rounded-[8px] " +
                (reason === null
                  ? "bg-light-surface-tertiary dark:bg-white/[0.06]"
                  : "bg-[#E5484D]")
              }
            >
              <Text
                className={
                  "font-sans font-semibold text-[14px] " +
                  (reason === null ? "text-light-text-tertiary dark:text-white/40" : "text-white")
                }
              >
                Report
              </Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    );

    if (IS_WEB) {
      return (
        <WebPortal>
          <View style={StyleSheet.absoluteFill}>{card}</View>
        </WebPortal>
      );
    }
    return (
      <Modal visible={open} transparent animationType="none" onRequestClose={onClose} accessibilityViewIsModal>
        {card}
      </Modal>
    );
  },
);

ReportReasonSheet.displayName = "ReportReasonSheet";
